

import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useState } from 'react';
import {
  APIActions,
  Box,
  Button,
  ContentTemplate,
  CustomDialog,
  MUIDataTable,
  MUIDataTableColumn,
  Progress,
  TableActionTypes,
  TableState,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
  useSnackbarHandler,
  CustomSelect
} from 'web-library';

import { TFunction, useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import CancelButton from 'web-library/components/buttons/CancelButton';
import requestStatusPill from 'web-library/stories/organism/Pill/RequestStatusPill';
import { getIndexValue } from 'web-library/utils/ArrayUtils';
import { stringToLongDateAndShortTime } from 'web-library/utils/DateUtils';
import { useGetTaskListFeatures } from '../../../api-refactored/user/useGetTaskListFeatures';
import { usePostProcessRequest } from '../../../api-refactored/user/usePostProcessRequest';
import { usePostSearchRequestOrTask } from '../../../api-refactored/user/usePostSearchRequestOrTask';
import { dropDownMappingToSelect, useAPIDropdown } from '../../../hooks/dropdown';
import { customFilterChipRender } from '../../../hooks/table';
import RouteKey from '../../../routes/key';
import { parseLocationSearch } from '../../../utils/StringUtils';
import { taskListShareStyle } from '../logic/useTaskData';
import TaskListUserTab from './TaskListUserTab';

const { viewDetails } = RouteKey.admin.child;

enum TabName {
  PENDING = 'Pending',
  ARCHIVED = 'Archived',
}

interface DataTableParams {
  state: TableState;
  classes: Record<string,string>;
  selectedTab: number;
  isRequest: boolean;
  dispatch: React.Dispatch<TableActionTypes>;
  t: TFunction<'translation'>;
  selectedActionType: string | undefined;
  selectedRequestType: string | undefined;
  selectedStatus: string | undefined;
  setSelectedUuid: React.Dispatch<React.SetStateAction<string>>;
  setOpenCancelModal: React.Dispatch<React.SetStateAction<boolean>>;
  setCancellingActionType: React.Dispatch<React.SetStateAction<string>>;
  history: RouteComponentProps['history'];
  filters: {
    filterMapping: {
      filterMappingActionType: any;
      filterMappingRequestType: any;
      filterMappingStatus: any;
    };
    filterCode: {
      filterCodeActionType: any;
      filterCodeRequestType: any;
      filterCodeStatus: any;
    };
  };
}

const useStyles = makeStyles()((theme) =>
({
  ...taskListShareStyle(theme),
  taskListTable:{
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
  },
  tabBox: {
    paddingBottom: theme.spacing(4),
  },
  byText: {
    color: theme.color.text.textTertiary,
  },
  viewDetailsButton: {
    padding: 0,
    justifyContent: 'flex-start',
  },
  iconButtonContainer: {
    display: 'inline-flex',
    fontSize: '1.125rem',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  cancelButton: {
    padding: '0 !important',
    justifyContent: 'flex-start',
  }
}));

const useRawFilters = () => {
  const queryResult = useGetTaskListFeatures();
  const actionType = useAPIDropdown(
    () => ({ data: queryResult.data?.action }),
    true,
    dropDownMappingToSelect
  );
  const requestType = useAPIDropdown(
    () => ({ data: queryResult.data?.requestType }),
    true,
    dropDownMappingToSelect
  );
  const status = useAPIDropdown(
    () => ({ data: queryResult.data?.status }),
    true,
    dropDownMappingToSelect
  );

  return {
    actionType,
    requestType,
    status,
  };
};

const displayDateTimeUpdated = (
  targetDateTime: string,
  by: string,
  dataIndex: number,
  classes: Record<string,string>
) => (
  <>
    {stringToLongDateAndShortTime(targetDateTime)}
    <br />
    <Typography className={classes.byText} variant="subtitle2">{`by ${by}`}</Typography>
  </>
);

const tabIndexByName = (tabName: string = '') => (tabName === TabName.ARCHIVED ? 1 : 0);

const useTableColumns = (params: DataTableParams): MUIDataTableColumn[] => [
  {
    name: 'actionType',
    label: params.t('admin->>TaskList.Action'),
    options: {
      sort: false,
      filter: true,
      filterType: 'custom',
      customFilterListOptions: {
        render: (v: any) => [params.filters.filterMapping.filterMappingActionType[v[0]].children],
        update: (filterList: string[][],
          filterPos: number,
          index: number) => {
          // update table filter state
          params.dispatch({
            type: 'UPDATE_FILTERS',
            payload: {
              ...params.state.filters,
              actionType: params.state.filters.actionType,
            },
          });

          // update table inner filter
          return customFilterChipRender(filterList, filterPos, index);
        },
      },
      filterOptions: {
        fullWidth: true,
        display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
          <CustomSelect
              rootClass={params.classes.selectRoot}
            className={params.classes.select}
            // eslint-disable-next-line react/destructuring-assignment
            defaultValue={params.selectedActionType}
            displayEmpty
            menuItems={params.filters.filterCode.filterCodeActionType}
            onChange={(event) => {
              // eslint-disable-next-line no-param-reassign
              filterList[index][0] = event.target.value as string;
              onChange(filterList[index], index, column);
            }}
            label={params.t('admin->>TaskList.ActionType')}
            labelId="inputSelectLabel-action"
            id="inputSelect-action"
          />
        ),
      },
    },
  },
  {
    name: 'requestType',
    label: params.t('admin->>TaskList.RequestType'),
    options: {
      sort: false,
      filter: true,
      filterType: 'custom',
      customFilterListOptions: {
        render: (v: any) => [params.filters.filterMapping.filterMappingRequestType[v[0]].children],
        update: (filterList: string[][],
          filterPos: number,
          index: number) => {
          // update table filter state
          params.dispatch({
            type: 'UPDATE_FILTERS',
            payload: {
              ...params.state.filters,
              requestType: params.state.filters.requestType,
            },
          });

          // update table inner filter
          return customFilterChipRender(filterList, filterPos, index);
        },
      },
      filterOptions: {
        fullWidth: true,
        display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
          <CustomSelect
              rootClass={params.classes.selectRoot}
            className={params.classes.select}
            // eslint-disable-next-line react/destructuring-assignment
            defaultValue={params.selectedRequestType}
            displayEmpty
            menuItems={params.filters.filterCode.filterCodeRequestType}
            onChange={(event) => {
              // eslint-disable-next-line no-param-reassign
              filterList[index][0] = event.target.value as string;
              onChange(filterList[index], index, column);
            }}
            label={params.t('admin->>TaskList.RequestType')}
            labelId="inputSelectLabel-request"
            id="inputSelect-request"
          />
        ),
      },
    },
  },
  {
    name: 'createdAt',
    label: params.t('admin->>TaskList.RequestDateTime'),
    options: {
      sort: true,
      filter: false,
      customBodyRenderLite: (dataIndex: number) =>
        displayDateTimeUpdated(
          params.state.data[dataIndex].createdAt,
          params.state.data[dataIndex].requestBy,
          dataIndex,
          params.classes
        ),
    },
  },
  {
    name: 'statusUpdatedBy',
    label: params.t('admin->>TaskList.Approver'),

    options: {
      sort: false,
      filter: false,
      display: params.selectedTab === 0 && params.isRequest, // if selected tab is "PENDING" and in Requestor flow
    },
  },
  {
    name: 'updatedAt',
    label: params.t('admin->>TaskList.ApprovedRejectedDateTime'),
    options: {
      filter: false,
      display: params.selectedTab !== 0, // if selected tab is "ARCHIVED"
      sort: true,
      customBodyRenderLite: (dataIndex: number) =>
        displayDateTimeUpdated(
          params.state.data[dataIndex].updatedAt,
          params.state.data[dataIndex].statusUpdatedBy,
          dataIndex,
          params.classes
        ),
    },
  },
  {
    name: 'status',
    label: params.t('admin->>TaskList.Status'),
    options: {
      sort: false,
      filter: true,
      filterType: 'custom',
      customBodyRenderLite: (dataIndex: number) =>
        requestStatusPill(params.state.data[dataIndex].requestStatus),
      customFilterListOptions: {
        render: (v: any) => [params.filters.filterMapping.filterMappingStatus[v[0]].children],
        update: (filterList: string[][],
          filterPos: number,
          index: number) => {
          // update table filter state
          params.dispatch({
            type: 'UPDATE_FILTERS',
            payload: { ...params.state.filters, state: params.state.filters.status },
          });

          // update table inner filter
          return customFilterChipRender(filterList, filterPos, index);
        },
      },
      filterOptions: {
        fullWidth: true,
        display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
          <CustomSelect
              rootClass={params.classes.selectRoot}
            className={params.classes.select}
            // eslint-disable-next-line react/destructuring-assignment
            defaultValue={params.selectedStatus}
            displayEmpty
            menuItems={params.filters.filterCode.filterCodeStatus}
            onChange={(event) => {
              // eslint-disable-next-line no-param-reassign
              filterList[index][0] = event.target.value as string;
              onChange(filterList[index], index, column);
            }}
            label={params.t('admin->>TaskList.Status')}
            labelId="inputSelectLabel-request"
            id="inputSelect-request"
          />
        ),
      },
    },
  },
  {
    name: 'action',
    label: params.t('admin->>TaskList.Actions'),
    options: {
      sort: false,
      filter: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Box className={params.classes.iconButtonContainer}>
          <Button
            onClick={() =>
              params.history.push(
                `${params.history.location.pathname}/${params.state.data[dataIndex].uuid}/${viewDetails.route}`
              )
            }
            className={params.classes.viewDetailsButton}
            variant="text"
            color="primary"
            size="small"
          >
            {params.t('admin->>TaskList.ViewDetails')}
          </Button>
          {(params.isRequest && params.selectedTab) === 0 && (
            <Box className={params.classes.iconButtonContainer}>
              <CancelButton
                className={params.classes.cancelButton}
                cancelHandler={() => {
                  params.setOpenCancelModal(true);
                  params.setSelectedUuid(params.state.data[dataIndex].uuid);
                  params.setCancellingActionType(params.state.data[dataIndex].actionType);
                }}
                label={params.t('admin->>TaskList.Cancel')}
              />
            </Box>
          )}
        </Box>
      ),
    },
  },
];

interface TaskListType {
  isRequest?: boolean;
}

const TaskList = ({ isRequest = false }: TaskListType) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<{ requestStatus: string; selectedTab: number }>();
  const locationState = location.state;

  const { requestStatus } = parseLocationSearch(location.search);
  const [currentTab, setCurrentTab] = useState(
    locationState?.selectedTab ?? tabIndexByName(requestStatus)
  );

  const { snackbarHandler } = useSnackbarHandler();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    page: 0,
    size: 5,
    sortColumn: currentTab === 0 ? 'createdAt' : 'updatedAt',
    order: currentTab === 0 ? 'asc' : 'desc',
  });

  const {
    mutate: mutateRequestTaskListing,
    isLoading: isLoadingRequestTaskListing,
  } = usePostSearchRequestOrTask({
    onSuccess: (data) => {
      history.replace(
        `${location.pathname}?requestStatus=${currentTab === 0 ? TabName.PENDING : TabName.ARCHIVED
        }`,
        locationState
      );
      return tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);

  const [selectedActionType, setSelectedActionType] = useState();
  const [selectedRequestType, setSelectedRequestType] = useState();
  const [selectedStatus, setSelectedStatus] = useState();

  const [cancellingActionType, setCancellingActionType] = useState('');
  const [selectedUuid, setSelectedUuid] = useState('');
  const { classes } = useStyles();

  const {
    actionType: { functionCode: filterCodeActionType, typeMapping: filterMappingActionType },
    requestType: { functionCode: filterCodeRequestType, typeMapping: filterMappingRequestType },
    status: { functionCode: filterCodeStatus, typeMapping: filterMappingStatus },
  } = useRawFilters();

  const cancelRequestMutation = usePostProcessRequest({
    onSuccess: () => {
      refreshRequestTaskListing();
    },
  });

  const tableColumns = useTableColumns({
    state,
    classes,
    selectedTab: currentTab,
    isRequest,
    dispatch: tableDispatch,
    t,
    selectedActionType,
    selectedRequestType,
    selectedStatus,
    setSelectedUuid,
    setOpenCancelModal,
    setCancellingActionType,
    history,
    filters: {
      filterMapping: {
        filterMappingActionType,
        filterMappingRequestType,
        filterMappingStatus,
      },
      filterCode: {
        filterCodeActionType,
        filterCodeRequestType,
        filterCodeStatus,
      },
    },
  });
  const refreshRequestTaskListing = useCallback(() => {
    if (!state.sortColumn || !state.order) {
      return;
    }
    mutateRequestTaskListing({
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        sort: state.sortColumn,
        order: state.order,
      },
      actionType: getIndexValue(state.filters.actionType, 0) || null,
      requestType: getIndexValue(state.filters.requestType, 0) || null,
      status: getIndexValue(state.filters.status, 0) || null,
      isRequest,
      isPending: currentTab === 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mutateRequestTaskListing,
    state.page,
    state.size,
    state.sortColumn,
    state.order,
    state.filters.actionType,
    state.filters.requestType,
    state.filters.status,
    isRequest,
    currentTab,
  ]);

  useEffect(() => {
    setSelectedActionType(state.filters.actionType);
    setSelectedRequestType(state.filters.requestType);
    setSelectedStatus(state.filters.status);
    refreshRequestTaskListing();
  }, [
    refreshRequestTaskListing,
    state.filters.actionType,
    state.filters.requestType,
    state.filters.status,
  ]);

  // To prevent multiple API calls on load
  const [isInit, setIsInit] = useState(true);
  useEffect(() => {
    if (isInit) {
      setIsInit(false);
      return;
    }

    tableDispatch({
      type: 'UPDATE_FILTERS',
      payload: {},
    } as TableActionTypes);

    tableDispatch({
      type: 'UPDATE_SORT',
      payload: {
        sortColumn: 'createdAt',
        order: 'asc',
      },
    } as TableActionTypes);

    setCurrentTab(0);

    mutateRequestTaskListing({
      apiPathParam: {
        page: 0,
        pageSize: 5,
        sort: 'createdAt',
        order: 'asc',
      },
      actionType: null,
      requestType: null,
      status: null,
      isRequest,
      isPending: true,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequest, tableDispatch]);

  const onChangeTab = useCallback(
    (event: any, newValue: any) => {
      tableDispatch({
        type: 'UPDATE_FILTERS',
        payload: {},
      } as TableActionTypes);

      tableDispatch({
        type: 'UPDATE_SORT',
        payload: {
          sortColumn: newValue === 0 ? 'createdAt' : 'updatedAt',
          order: newValue === 0 ? 'asc' : 'desc',
        },
      } as TableActionTypes);
      setCurrentTab(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableDispatch]
  );

  useEffect(() => {
    if (locationState?.requestStatus === 'Approved') {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>TaskDetails.RequestApproved'),
      });
      history.replace({ state: {} });
    }
    if (locationState?.requestStatus === 'Rejected') {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('admin->>TaskDetails.RequestRejected'),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
      history.replace({ state: {} });
    }
  }, [history, locationState?.requestStatus, snackbarHandler, t]);

  const handleCloseCancelModal = () => setOpenCancelModal(false);
  const handleCancelRequest = () => {
    cancelRequestMutation.mutate({
      status: 'CANCELLED',
      requestUuid: selectedUuid,
    });
    handleCloseCancelModal();
  };

  if (isLoadingRequestTaskListing) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <Box>
      <ContentTemplate type="full" continuous isContinuable>
        <Box id="task-list-table" className={classes.taskListTable}>
          <Box className={classes.tabBox}>
            <TaskListUserTab
              selectedTab={currentTab}
              setSelectedTab={setCurrentTab}
              onChangeTab={onChangeTab}
            />
          </Box>
          <MUIDataTable
            state={state}
            dispatch={tableDispatch}
            columns={tableColumns}
            data={state.data}
            options={{
              ...tOptions,
              filter: true,
              sort: true,
              pagination: true,
              sortOrder: {
                name: state.sortColumn,
                direction: state.order,
              },
            }}
            rowsPerPageOptions={[5, 10, 20, 40, 50]}
          />
        </Box>

        <CustomDialog
          open={openCancelModal}
          title={t('admin->>TaskList.CancelModalTitle')}
          message={t('admin->>TaskList.CancelModalDescription', { action: cancellingActionType })}
          action={
            <APIActions
              onClose={handleCloseCancelModal}
              apiCall={handleCancelRequest}
              cancelString={t('admin->>TaskList.Cancel')}
              apiString={t('admin->>TaskList.ProceedButtonText')}
            />
          }
        />
      </ContentTemplate>
    </Box>
  );
};

export default TaskList;
